import {
  useCreateOne,
  useDeleteOne,
  useGetList,
  useGetOne,
  useUpdateOne,
} from "#src/components/hooks/adapters/adapterUtils";
import { useStickyState } from "#src/hooks/useStickyState";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import {
  FormSubmissionAdapter,
  FormSubmissionType,
  getFilterObject,
  Resources,
  type FormSubmissionGetListRequestType,
} from "@validereinc/domain";
import { BaseError, downloadLink } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import { produce } from "immer";
import React, { useMemo } from "react";

export const useGetOneFormSubmission = useGetOne<FormSubmissionType>(
  FormSubmissionAdapter.getOne,
  Resources.FORM_SUBMISSION
);

export const useListFormSubmissions = useGetList(
  FormSubmissionAdapter.getList,
  Resources.FORM_SUBMISSION
);

export const useDeleteOneFormSubmission = useDeleteOne<FormSubmissionType>(
  FormSubmissionAdapter.deleteOne,
  Resources.FORM_SUBMISSION
);

export const useCreateOneFormSubmission = useCreateOne<FormSubmissionType>(
  FormSubmissionAdapter.createOne,
  Resources.FORM_SUBMISSION
);

export const useUpdateOneFormSubmission = useUpdateOne<FormSubmissionType>(
  FormSubmissionAdapter.updateOne,
  Resources.FORM_SUBMISSION
);

export const useCreateOneFormSubmissionRevision =
  useCreateOne<FormSubmissionType>(
    FormSubmissionAdapter.revision.createOne,
    Resources.FORM_SUBMISSION
  );

// Exports all form submissions for a template. This is different from useForms useExportFormSubmission
export const useExportFormSubmissions = (
  apiParams: FormSubmissionGetListRequestType = {}
) => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const report = await FormSubmissionAdapter.exportList?.(apiParams);
      if (!report?.s3_download_link) {
        throw new BaseError(
          `Could not fetch download URL for form submissions export`,
          { cause: report }
        );
      }
      downloadLink(report.s3_download_link);
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export form submissions.",
      });
    },
  });
};

export const useBulkDeleteFormSubmissions = ({
  jobKey,
  formSubmissionGetListParams,
  selectedFormSubmissions,
  isSelectionInverted,
  setIsDeleting,
}: {
  jobKey: string;
  formSubmissionGetListParams: Parameters<
    typeof FormSubmissionAdapter.getList
  >["0"];
  selectedFormSubmissions: Record<string, FormSubmissionType>;
  isSelectionInverted: boolean;
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>;
}): {
  handleBulkDelete: () => Promise<void>;
} => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  const bulkDeleteFilters = useMemo(() => {
    if (isSelectionInverted) {
      return formSubmissionGetListParams.filters as FormSubmissionGetListRequestType["filters"];
    }

    return produce(formSubmissionGetListParams, (draft) => {
      draft.filters = getFilterObject(formSubmissionGetListParams.filters);
      draft.filters.isAlreadyFormatted = true;

      if (!isEmpty(selectedFormSubmissions)) {
        draft.filters.id = {
          $in: Object.keys(selectedFormSubmissions),
        };
      }
    }).filters as FormSubmissionGetListRequestType["filters"];
  }, [
    formSubmissionGetListParams.filters,
    JSON.stringify(selectedFormSubmissions),
    isSelectionInverted,
  ]);

  const bulkDeleteMutation = useMutation({
    mutationFn: () =>
      FormSubmissionAdapter.bulkDelete?.({
        filters: bulkDeleteFilters,
      }),
    onSuccess: (result) => {
      setJobId(result.job.id);
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to bulk delete form submissions.",
      });
      setIsDeleting(false);
    },
  });

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    await bulkDeleteMutation.mutateAsync();
  };

  return {
    handleBulkDelete,
  };
};

export const useClearFormSubmissionCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey: [Resources.FORM_SUBMISSION] });
    },
  };
};
